import { Link } from "react-router-dom";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { Section, Eyebrow } from "@/components/SharedUI";

// Each beat is [Yuwaalaraay original, English translation]
// First element of pair can also be a parenthetical aside (italic, smaller).
const beats = [
  { gam: "Bigibila yanaa-waa-nhi, biyaduul.", en: "A porcupine was walking along by himself." },
  { gam: "Bulaarr badjin mindjarru yanaa-waa-nhi.", en: "Two little weebills were walking along." },
  { aside: "(Giirr yilaalu nhama mindjarru, bigibila dhayn gi-gi-la-nhi.)", asideEn: "(Long ago, the weebills and Bigibila were people.)" },
  { narration: "One day, a weebill threw a boomerang at a dhinawan (emu)." },
  { gam: "Giirr bundaa-nhi nhama dhinawan. Bamba ngaama bundaa-nhi.", en: "The emu fell down with a great crash." },
  { narration: "Bigibila heard the sound and called out:" },
  { gam: "\"Aa, minya ngaama bundaa-nhi?\"", en: "\"What fell over there?\"" },
  { narration: "The two mindjarru replied:" },
  { gam: "\"Waal, waal baayamba. Waal, baayamba.\"", en: "\"No, no friend. Nothing friend.\"" },
  { narration: "But Bigibila knew better. He called out:" },
  { gam: "\"Waa, waa, waa, waa. Maayndjul dhinggaa!\"", en: "\"Waa, waa, waa, waa. Lovely meat!\"" },
  { narration: "Bigibila took the emu and began cooking it in the ground. While the emu cooked, he instructed the two mindjarru to gather leaves from the bush." },
  { gam: "\"Ngarraagulay-nga yanaa-ya, girran.girraa-gu.\"", en: "\"Go over there and gather leaves.\"" },
  { narration: "The two little mindjarru ran through the bush searching for leaves. Whenever they returned and asked:" },
  { gam: "\"Nhalay-gaa ngaan.gii?\"", en: "\"What about these, friend?\"" },
  { narration: "Bigibila answered:" },
  { gam: "\"Ngaangaarran-gu yanaa-ya.\"", en: "\"Go further.\"" },
  { narration: "Again and again he sent them away. While they searched, Bigibila finished cooking the emu, removed it from the earth and began eating it himself." },
  { narration: "When the two mindjarru finally returned, hungry and tired, they called:" },
  { gam: "\"Yaama-nga ngaan.gii!\"", en: "\"Hello friend!\"" },
  { narration: "Then they said:" },
  { gam: "\"Giirr ngali-nga maayrr dha-lda-nha.\"", en: "\"We have nothing to eat.\"" },
  { narration: "One of them asked:" },
  { gam: "\"Ngaa, gana-badhaay ngay wuu-na.\"", en: "\"Please give me the liver.\"" },
  { narration: "But Bigibila refused. Instead he continued saying:" },
  { gam: "\"Waa, waa, waa, waa. Maayndjul dhinggaa!\"", en: "\"Lovely meat!\"" },
  { narration: "He would not share. Not even a small piece. The two little mindjarru left sadly." },
  { narration: "As they travelled, they came across the buurrngan (meat ants)." },
  { aside: "(Giirr ganunga-bula dhayn gi-gi-la-nhi, nhama buurrngan.)", asideEn: "(In those days, the meat ants were people too.)" },
  { narration: "The mindjarru told them:" },
  { gam: "\"Waal ngaan.gii-dju minyagaa ngalingu wuu-rri.\"", en: "\"Old mate won't give us anything.\"" },
  { gam: "\"Waal ngay gana wuu-nhi, waal ngay gii wuu-nhi.\"", en: "\"He didn't give us the heart. He didn't give us the liver.\"" },
  { narration: "The buurrngan listened. Then they replied:" },
  { gam: "\"Ngaayaybaay.\"", en: "\"Alright.\"" },
  { narration: "The meat ants gathered their spears and travelled together. When they found Bigibila, they confronted him. They speared him. Again. And again. And again. Soon, countless spears were embedded throughout his body." },
  { narration: "As they struck him, Bigibila cried out:" },
  { gam: "\"Nginaalingu dhinggaa! Nginu dhinggaa!\"", en: "\"This meat is for you! Your meat!\"" },
  { narration: "But it was too late. The spears remained where they had landed. Those spears became his wiyayl — his quills." },
  { narration: "And from that day forward:" },
  { gam: "Yalagiyu Bigibila yanaay-la-nha wiyayl-bil, bilaarr-bil.", en: "Bigibila walked covered in quills, covered in spears.", emphasis: true },
];

const lessons = [
  ["Responsibility", "Leadership carries obligations to those who walk beside you."],
  ["Fairness", "Resources, food and knowledge are meant to be shared."],
  ["Stewardship", "Community sees how power is used — and remembers."],
  ["Accountability", "Actions have consequences that arrive in their own time."],
];

export default function Welcome() {
  return (
    <>
      {/* HERO — Yaama Welcome */}
      <section className="border-b border-stone-200 relative overflow-hidden">
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6">
            <div className="text-[11px] tracking-[0.32em] uppercase font-bold text-[#9C4221] mb-4 inline-flex items-center gap-2">
              <span className="h-1.5 w-1.5 bg-[#9C4221] rounded-full" />
              Yaama <span className="text-stone-500 font-normal normal-case tracking-normal italic">— Welcome</span>
            </div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl tracking-tighter font-bold text-stone-900 leading-[0.95]">
              Welcome to<br />
              <span className="text-stone-400">Bigibila Systems™.</span>
            </h1>
            <p className="mt-8 text-stone-700 leading-relaxed max-w-xl text-lg md:text-xl">
              Before you enter, take a moment to read the story Bigibila carries. This is <span className="italic">Bigibila Wiyayl</span> — how the porcupine got his quills — told in Yuwaalaraay by <span className="text-stone-900 font-semibold">Arthur Dodd</span>, Lore man and great-great-grandfather of Liana Pitt.
            </p>
            <a
              href="#story"
              data-testid="welcome-read-story-cta"
              className="mt-10 inline-flex items-center gap-2 text-sm font-medium text-stone-900 border-b border-stone-300 hover:border-stone-900 pb-1 transition-colors"
            >
              Read Bigibila Wiyayl <ArrowDown className="h-4 w-4" />
            </a>
          </div>
          <div className="lg:col-span-6">
            <div className="relative">
              <img
                src="/brand/bigibila-lingo.jpg"
                alt="Echidna in my lingo is Bigibila — Big-ee-billa. Illustration by Briana Pitt."
                className="w-full h-auto"
              />
              <figcaption className="mt-4 text-xs text-stone-600 leading-relaxed border-t border-stone-200 pt-3">
                <span className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#9C4221] mr-2">Artwork</span>
                Art by First Nations Artist / Designer{" "}
                <span className="text-stone-900 font-semibold">Briana Pitt</span>{" "}
                — Gamilaraay <span className="italic">Yinaar</span> (Woman).
              </figcaption>
            </div>
          </div>
        </div>
      </section>

      {/* STORY */}
      <section id="story" className="border-b border-stone-200 scroll-mt-20">
        <Section>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <div className="lg:sticky lg:top-28">
                <Eyebrow>The story</Eyebrow>
                <h2 className="font-display text-4xl md:text-5xl tracking-tighter font-bold text-stone-900 leading-tight">
                  Bigibila Wiyayl
                </h2>
                <p className="mt-3 text-stone-600 italic">
                  The Story of the Porcupine's Quills.
                </p>
                <div className="mt-6 border-l-2 border-[#9C4221]/40 pl-4 py-1 max-w-sm">
                  <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#9C4221] mb-1">Told by</div>
                  <div className="font-display font-bold text-stone-900">Arthur Dodd</div>
                  <div className="text-xs text-stone-600 mt-0.5">Yuwaalaraay Lore man · great-great-grandfather of Liana Pitt</div>
                </div>
                <div className="mt-4 border border-stone-200 bg-stone-50 p-4 max-w-sm">
                  <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-stone-500 mb-1.5">Language reference</div>
                  <div className="text-sm text-stone-800 font-semibold">Australian National University (ANU)</div>
                  <div className="text-[11px] text-stone-600 mt-1 leading-relaxed">
                    Yuwaalaraay language sources and orthography drawn from work documented and held at ANU.
                  </div>
                </div>
                <p className="mt-6 text-sm text-stone-700 leading-relaxed">
                  Presented in <span className="text-stone-900 font-semibold">Yuwaalaraay</span> and English. Read carefully — the lesson is not in the events, it is in what they ask of us.
                </p>
              </div>
            </div>

            <div className="lg:col-span-8 space-y-8">
              {beats.map((b, i) => {
                const keyText = b.gam || b.narration || b.aside;
                const beatKey = `beat-${i}-${keyText.slice(0, 24)}`;
                if (b.aside) {
                  return (
                    <div key={beatKey} className="border-l-2 border-stone-300 pl-5 py-1 max-w-2xl">
                      <p className="font-display italic text-stone-700">{b.aside}</p>
                      <p className="text-sm text-stone-500 italic mt-1">{b.asideEn}</p>
                    </div>
                  );
                }
                if (b.narration) {
                  return (
                    <p key={beatKey} className="text-stone-800 leading-[1.85] text-base md:text-lg max-w-2xl">
                      {b.narration}
                    </p>
                  );
                }
                return (
                  <div
                    key={beatKey}
                    className={`grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 ${b.emphasis ? "border border-[#9C4221]/30 bg-[#9C4221]/[0.06] p-6" : ""}`}
                  >
                    <p className={`font-display ${b.emphasis ? "text-xl md:text-2xl" : "text-lg"} text-stone-900 leading-snug italic`}>
                      {b.gam}
                    </p>
                    <p className={`text-stone-600 ${b.emphasis ? "text-lg md:text-xl text-stone-800" : "text-base"} leading-relaxed`}>
                      {b.en}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </Section>
      </section>

      {/* PHOTO BAND — bigibila on Country */}
      <section className="border-b border-stone-200 relative">
        <div className="relative h-[40vh] min-h-[320px] md:h-[55vh] overflow-hidden">
          <img
            src="/brand/bigibila-photo.jpg"
            alt="Bigibila walking on Country"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-stone-950/65 via-stone-950/15 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0">
            <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 pb-10">
              <div className="text-[10px] tracking-[0.32em] uppercase font-bold text-[#E4B58E] mb-3">Wiyayl</div>
              <p className="font-display text-2xl md:text-4xl tracking-tight font-bold text-white leading-tight max-w-3xl">
                The quills are not decoration. They are the record of what was taken without sharing.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* MEANING */}
      <section className="border-b border-stone-200 bg-stone-50">
        <Section>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <Eyebrow>The meaning</Eyebrow>
              <h2 className="font-display text-3xl md:text-4xl tracking-tight font-bold text-stone-900 leading-tight">
                Governance without responsibility eventually becomes visible to everyone.
              </h2>
              <p className="mt-6 text-stone-700 leading-relaxed max-w-lg">
                Bigibila Wiyayl is a story about responsibility, fairness, stewardship and accountability. The quills carried by Bigibila are reminders — what is taken without sharing returns as a mark on the body of the leader.
              </p>
              <div className="mt-8 aspect-[4/3] overflow-hidden border border-stone-200">
                <img
                  src="/brand/bigibila-quills.jpg"
                  alt="Close-up of bigibila quills"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="mt-6 text-[11px] tracking-[0.32em] uppercase font-bold text-[#9C4221]">
                Yaama. Gulibaa.
                <div className="text-stone-500 font-normal normal-case tracking-normal italic text-[12px] mt-1">
                  Welcome. Remember.
                </div>
              </div>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-px bg-stone-200 border border-stone-200 self-start">
              {lessons.map(([title, desc]) => (
                <div key={title} className="bg-[#FAFAF9] p-7">
                  <h3 className="font-display font-bold text-xl tracking-tight text-[#9C4221]">{title}</h3>
                  <p className="mt-3 text-sm text-stone-700 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </Section>
      </section>

      {/* CONTINUE CTA */}
      <section className="border-b border-stone-200">
        <Section>
          <div className="max-w-3xl">
            <Eyebrow>Now you may enter</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl tracking-tighter font-bold text-stone-900 leading-tight">
              Carry the lesson into the system.
            </h2>
            <p className="mt-6 text-stone-700 leading-relaxed text-lg">
              The story you've just read is the foundation of everything that follows. The quills are the governance logic. Protection. Boundary. Consequence. Survival. Bigibila Systems™ holds these in software so they cannot be forgotten.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/start"
                data-testid="welcome-enter-cta"
                className="inline-flex items-center gap-2 bg-stone-900 text-white px-6 py-3.5 text-sm font-medium rounded-sm hover:bg-stone-800 transition-colors"
              >
                Enter Bigibila Systems <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link
                to="/story"
                data-testid="welcome-deeper-story-cta"
                className="inline-flex items-center gap-2 border border-stone-300 px-6 py-3.5 text-sm font-medium rounded-sm hover:border-stone-900 transition-colors text-stone-900"
              >
                Read the lineage & deeper context
              </Link>
            </div>
            <p className="mt-10 text-xs text-stone-500 italic max-w-lg">
              With respect to all Custodians, Elders and Old People — past, present and emerging.
            </p>
          </div>
        </Section>
      </section>
    </>
  );
}
